import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface AnimationState {
  prefersReducedMotion: boolean;
  animationsEnabled: boolean;
  loadingStates: Record<string, boolean>;
  errors: Record<string, string>;
}

type AnimationAction =
  | { type: 'SET_REDUCED_MOTION'; payload: boolean }
  | { type: 'TOGGLE_ANIMATIONS' }
  | { type: 'SET_LOADING'; payload: { id: string; loading: boolean } }
  | { type: 'SET_ERROR'; payload: { id: string; error: string } }
  | { type: 'CLEAR_ERROR'; payload: string }
  | { type: 'CLEAR_ALL_ERRORS' };

const initialState: AnimationState = {
  prefersReducedMotion: false,
  animationsEnabled: true,
  loadingStates: {},
  errors: {}
};

function animationReducer(state: AnimationState, action: AnimationAction): AnimationState {
  switch (action.type) {
    case 'SET_REDUCED_MOTION':
      return {
        ...state,
        prefersReducedMotion: action.payload,
        animationsEnabled: !action.payload
      };
    case 'TOGGLE_ANIMATIONS':
      return {
        ...state,
        animationsEnabled: !state.animationsEnabled
      };
    case 'SET_LOADING':
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.payload.id]: action.payload.loading
        }
      };
    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: action.payload.error
        }
      };
    case 'CLEAR_ERROR': {
      const { [action.payload]: removed, ...remainingErrors } = state.errors;
      return {
        ...state,
        errors: remainingErrors
      };
    }
    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        errors: {}
      };
    default:
      return state;
  }
}

interface AnimationContextType {
  state: AnimationState;
  dispatch: React.Dispatch<AnimationAction>;
  setReducedMotion: (enabled: boolean) => void;
  toggleAnimations: () => void;
  setLoading: (id: string, loading: boolean) => void;
  setError: (id: string, error: string) => void;
  clearError: (id: string) => void;
  clearAllErrors: () => void;
  isLoading: (id: string) => boolean;
  getError: (id: string) => string | undefined;
}

const AnimationContext = createContext<AnimationContextType | undefined>(undefined);

export function useAnimation() {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimation must be used within an AnimationProvider');
  }
  return context;
}

interface AnimationProviderProps {
  children: ReactNode;
}

export function AnimationProvider({ children }: AnimationProviderProps) {
  const [state, dispatch] = useReducer(animationReducer, initialState);

  // Action creators
  const setReducedMotion = (enabled: boolean) => {
    dispatch({ type: 'SET_REDUCED_MOTION', payload: enabled });
  };

  const toggleAnimations = () => {
    dispatch({ type: 'TOGGLE_ANIMATIONS' });
  };

  const setLoading = (id: string, loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: { id, loading } });
  };

  const setError = (id: string, error: string) => {
    dispatch({ type: 'SET_ERROR', payload: { id, error } });
  };

  const clearError = (id: string) => {
    dispatch({ type: 'CLEAR_ERROR', payload: id });
  };

  const clearAllErrors = () => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  };

  // Selectors
  const isLoading = (id: string) => state.loadingStates[id] || false;
  const getError = (id: string) => state.errors[id];

  // Initialize reduced motion preference
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mediaQuery.matches);
      
      const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const value: AnimationContextType = {
    state,
    dispatch,
    setReducedMotion,
    toggleAnimations,
    setLoading,
    setError,
    clearError,
    clearAllErrors,
    isLoading,
    getError
  };

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
}